import React from 'react';
import { formatCredits } from '../../utils/credits';

/**
 * The terms of the purchase currently live on an order: when it screens, how
 * long the hire runs, what it cost.
 *
 * Reads the purchase rather than the order's top-level fields. Those fields
 * belong to whichever purchase last wrote them, so on a re-ordered title they
 * showed the previous purchase's correction — a screening date and price the
 * cinema had never agreed to for this booking.
 *
 * Where BAC changed what the cinema asked for, both are shown: the request
 * struck through, the agreed value beside it.
 */
const formatDate = value => (value ? new Date(value).toLocaleString() : '—');

const PurchaseTerms = ({ purchase, version }) => {
    if (!purchase) {
        return (
            <div className="text-white-50 small">
                <span className="fas fa-circle-info me-2" />
                This title has not been purchased yet.
            </div>
        );
    }

    const dateChanged = purchase.requestedScreeningDate
        && purchase.screeningDate
        && new Date(purchase.requestedScreeningDate).getTime() !== new Date(purchase.screeningDate).getTime();

    const priceChanged = purchase.requestedPrice != null
        && purchase.price != null
        && purchase.requestedPrice !== purchase.price;

    const hireChanged = purchase.hirePeriod != null
        && version?.defaultHirePeriod != null
        && purchase.hirePeriod !== version.defaultHirePeriod;

    return (
        <>
            <div className="mb-3">
                <div className="text-white-50 small mb-1">Screening Date</div>
                {dateChanged && (
                    <div className="text-white-50 fw-bold text-decoration-line-through">
                        <span className="fas fa-calendar me-2 text-primary" />
                        {formatDate(purchase.requestedScreeningDate)}
                    </div>
                )}
                <div className="text-white fw-bold">
                    <span className={`fas ${dateChanged ? 'fa-calendar-check text-success' : 'fa-calendar text-primary'} me-2`} />
                    {formatDate(purchase.screeningDate)}
                </div>
            </div>

            <div className="mb-3">
                <div className="text-white-50 small mb-1">Hire Period</div>
                <div className="text-white fw-bold">
                    <span className="fas fa-clock me-2 text-primary" />
                    {hireChanged && (
                        <>
                            <span className="text-decoration-line-through text-white-50">
                                {version.defaultHirePeriod} {version.hirePeriodType}
                            </span>
                            <span className="fas fa-chevron-right px-2 text-success" />
                        </>
                    )}
                    {purchase.hirePeriod != null
                        ? `${purchase.hirePeriod} ${purchase.hirePeriodType || ''}`.trim()
                        : '—'}
                </div>
            </div>

            {purchase.price != null && (
                <div className="mb-3">
                    <div className="text-white-50 small mb-1">Price</div>
                    <div className="text-primary fw-bold">
                        <span className="fas fa-coins me-2" />
                        {priceChanged && (
                            <span className="text-decoration-line-through text-danger me-2">
                                {formatCredits(purchase.requestedPrice)}
                            </span>
                        )}
                        {formatCredits(purchase.price)} credits
                    </div>
                </div>
            )}
        </>
    );
};

export default PurchaseTerms;
